import React, { useRef, useState } from "react";
import Slider from "react-slick";
import {
  Card,
  CardMedia,
  Typography,
  Button,
  Box,
  Grid,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";

const isVideo = (url) => {
  if (typeof url !== "string") return false;
  return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
};

export default function RentalYieldGridView({
  id,
  name,
  address,
  totalamount,
  amenities = [],
  nearby = [],
  persqft,
  images = [],
  verify,
  area,
  soldStatus,
  sold_status,
}) {
  const isSold = soldStatus === 1 || sold_status === 1;
  const previewSliderRef = useRef(null);
  const navi = useNavigate();
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = videoRefs.current[src];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const visibleAmenities = amenities.slice(0, 3);
  const visibleNearby = nearby.slice(0, 3);

  const validImages =
    Array.isArray(images) && images.length > 0
      ? images
      : ["/assets/images/no-image.png"];

  const showArrows = validImages.length > 1;

  // Pause all videos inside a given slick slider instance
  const pauseVideosIn = (sliderInstance) => {
    const videos =
      sliderInstance?.innerSlider?.list?.querySelectorAll("video");
    videos?.forEach((v) => {
      v.pause();
      v.currentTime = 0;
    });
  };


  const handleOpenPreview = (index) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  const handleClosePreview = () => {
    pauseVideosIn(previewSliderRef.current);
    setImgOpen(false);
  };

  return (
    <>
      {/* ── FULL-SCREEN PREVIEW DIALOG ── */}
      <Dialog
        open={imgOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", bgcolor: "rgba(0,0,0,0.85)" },
        }}
      >
        <IconButton
          onClick={handleClosePreview}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              position: "relative",
              height: { xs: 320, sm: 500, md: 620 },
              overflow: "hidden",
              // Lock ALL slick internals to the fixed height so video natural
              // height never collapses or jumps the container
              "& .slick-slider, & .slick-list, & .slick-track": {
                height: "100% !important",
              },
              "& .slick-slide > div": {
                height: "100% !important",
              },
            }}
          >
            {showArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{
                  position: "absolute", left: 12, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  color: "#fff", bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider
              ref={previewSliderRef}
              initialSlide={activeIndex}
              beforeChange={(_, next) => {
                pauseVideosIn(previewSliderRef.current);
                setActiveIndex(next);
              }}
              dots
              infinite
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              adaptiveHeight={false}
            >
              {validImages.map((img, i) => (
                <Box key={i} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: { xs: 320, sm: 500, md: 620 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.7)",
                      p: 1,
                      overflow: "hidden",
                      position: "relative"
                    }}
                  >
                    {!loadedMedia[img] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                    )}
                    {isVideo(img) ? (
                      <video
                        key={`preview-video-${i}`}
                        src={img}
                        controls
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload nopictureinpicture"
                        onLoadedData={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    ) : (
                      <img
                        src={img}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {showArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  color: "#fff", bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}

            {/* Slide counter */}
            {showArrows && (
              <Box
                sx={{
                  position: "absolute", bottom: 12, left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(0,0,0,0.5)", color: "#fff",
                  borderRadius: "12px", px: 1.5, py: 0.3,
                  fontSize: 13, pointerEvents: "none", zIndex: 3,
                }}
              >
                {activeIndex + 1} / {validImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── CARD ── */}
      <Card
        onClick={() => navi(`/rental-yield-plots/details/${id}`)}
        sx={{
          borderRadius: 4,
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "#FBFBF8",
          boxShadow: "0 4px 12px 6px rgba(0,0,0,0.08)",
          overflow: "hidden",
          transition: "box-shadow 0.25s ease, border-color 0.25s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box display="flex" flexDirection="column" flexGrow={1} gap={1}>

          {/* ── IMAGE / VIDEO SECTION ── */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              py: 1,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isSold && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: 16,
                  bgcolor: "#d32f2f",
                  color: "#fff",
                  px: 2,
                  py: 0.6,
                  borderRadius: 1.5,
                  fontSize: 13,
                  fontWeight: 800,
                  zIndex: 3,
                  textTransform: "uppercase",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                  letterSpacing: "0.5px",
                }}
              >
                Sold Out
              </Box>
            )}
            <Box
              sx={{
                px: 1,
                height: "100%",
                opacity: isSold ? 0.6 : 1,
                filter: isSold ? "grayscale(20%)" : "none",
              }}
            >
              {isVideo(validImages[0]) ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: 150, sm: 170, md: 180 },
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  {!loadedMedia[validImages[0]] && (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      sx={{ position: "absolute", top: 0, left: 0 }}
                    />
                  )}
                  <video
                    ref={(el) => (videoRefs.current[validImages[0]] = el)}
                    src={validImages[0]}
                    playsInline
                    disablePictureInPicture
                    disableRemotePlayback
                    controlsList="nodownload nopictureinpicture"
                    onLoadedData={() => handleMediaLoad(validImages[0])}
                    onClick={(e) => {
                      togglePlay(e, validImages[0]);
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: loadedMedia[validImages[0]] ? "block" : "none",
                      borderRadius: 3,
                      transition: "transform 0.3s ease",
                      cursor: "pointer",
                    }}
                  />
                  {loadedMedia[validImages[0]] && playingVideo !== validImages[0] && (
                    <IconButton
                      onClick={(e) => togglePlay(e, validImages[0])}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#fff",
                        bgcolor: "rgba(0,0,0,0.5)",
                        borderRadius: "4px",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <Box sx={{ position: "relative", width: "100%", height: { xs: 150, sm: 170, md: 180 } }}>
                  {!loadedMedia[validImages[0]] && (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      sx={{ position: "absolute", top: 0, left: 0, borderRadius: 3 }}
                    />
                  )}
                  <CardMedia
                    component="img"
                    image={validImages[0]}
                    onLoad={() => handleMediaLoad(validImages[0])}
                    onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 3,
                      objectFit: "cover",
                      cursor: "zoom-in",
                      display: loadedMedia[validImages[0]] ? "block" : "none",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* TITLE + LOCATION */}
          <Box sx={{ opacity: isSold ? 0.65 : 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box>
              <Typography
                fontWeight={600}
                sx={{ color: "#95B160", fontSize: { xs: 14, sm: 15, md: 16 } }}
              >
                {name || "Property Name"}
              </Typography>

              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationOn sx={{ fontSize: { xs: 14, sm: 16 }, color: "#9DC354", flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: "#707070",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {address || "No Location available"}
                </Typography>
              </Box>
            </Box>

            <Box>
              {verify === 1 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <img src="/assets/images/verified.png" alt="verified" width={20} height={20} />
                  <Typography fontSize={{ xs: 12, sm: 13 }} fontWeight={600}>
                    Verified Plot
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#FEB821", my: 0.8, borderWidth: 1.5 }} />

            {/* PRICE ROW */}
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <PriceItem label="Total Amount" value={totalamount || "-"} />
              <VDivider />
              <PriceItem label="Per sqft" value={persqft || "-"} />
            </Grid>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#FEB821", my: 0.8, borderWidth: 1.5 }} />

            <InfoRow title="Amenities" data={visibleAmenities} empty="No amenities" />
            <InfoRow title="Nearby" data={visibleNearby} empty="No nearby places" />
          </Box>

          <Box display="flex" justifyContent="center" mt="auto" pt={{ xs: 1, sm: 2 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#FEB821",
                color: "#000",
                borderRadius: 16,
                px: { xs: 5, sm: 8 },
                py: 1,
                fontSize: { xs: 12, sm: 13, md: 14 },
                textTransform: "capitalize",
                fontWeight: 600,
                opacity: isSold ? 0.65 : 1,
              }}
              onClick={(e) => { e.stopPropagation(); navi(`/rental-yield-plots/details/${id}`); }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
}

/* ── Sub-components ── */

const PriceItem = ({ label, value }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.5}>
      <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#95B160" }} />
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{value}</Typography>
    </Box>
    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
  </Box>
);

const VDivider = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{ borderStyle: "dashed", mx: 2, borderColor: "#FEB821", borderWidth: 1.5 }}
  />
);

const InfoRow = ({ title, data, empty }) => {
  const safeData = Array.isArray(data) ? data : [];
  return (
    <Box display="flex" gap={1} mt={1}>
      <Typography
        sx={{
          minWidth: 90,
          fontWeight: 500,
          fontSize: { xs: 12, sm: 13, md: 14 },
          whiteSpace: "nowrap",
        }}
      >
        {title}:
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 12, sm: 13, md: 14 },
          color: "text.secondary",
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {safeData.length > 0
          ? safeData.map((item, i) => (
            <span key={i}>
              {item?.title || item}
              {i !== safeData.length - 1 && " • "}
            </span>
          ))
          : empty}
      </Typography>
    </Box>
  );
};